import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, ProgressCircle, Flex } from '@adobe/react-spectrum';
import { format } from 'date-fns';

import { EditorState, convertToRaw } from 'draft-js';
import actionWebInvoke from '../../utils/actionCallUtils';
import actions from '../../config.json';
import OBJCODE from '../../constants';
import StaticProjectForm from './staticProjectForm';
import CustomField from '../../components/Forms/DynamicForms/customField';
import {
  Wrap,
  Title,
  Actions,
  Section,
  Page,
  Container,
} from '../../components/Forms/DynamicForms/style';
import fetchCustomFormsAndFields from '../../utils/commonFormUtils';
import FileComponent from '../../components/FileComponent/FileComponent';
import ContainerBlock from '../../components/FileComponent/fileComponentStyle';
import getWfFoldersAndFiles from '../../components/assets/workfrontfileutils';
import getAEMFoldersAndFiles, {
  createFolderInAEM,
} from '../../components/assets/aemfileutils';
import getHeaders from '../../utils/headers';
import { handleRootFolderData } from '../../components/FileComponent/fileHelper';

const DynamicProjectForm = ({ ims, user }) => {
  const [templates, setTemplates] = useState({
    isLoading: true,
    elements: [],
    selected: '',
  });
  const [isFileComponent, setFileComponent] = useState(false);
  const [rootFolder, setRootFolder] = useState();
  const [childFolder, setChildFolder] = useState('');
  const [wfObjectCodeAndID, setWfObjectCodeAndID] = useState({
    objCode: 'PROJ',
    objID: '',
  });
  const [values, setValues] = useState({});
  const [isSecondPage, setSecondPage] = useState(false);
  const [projectCustomFields, setProjectCustomFields] = useState();
  const [dynamicValues, setDynamicValues] = useState({});
  const [dateValues, setDateValues] = useState({});
  const [dateObject, setDateObject] = useState({});
  const [convertedContent, setConvertedContent] = useState(null);
  const [multipleValues, setMultipleValues] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty(),
  );
  const [workfrontUrl, setWorkfrontUrl] = useState('');

  const handlePageChange = () => {
    if (projectCustomFields?.length === 0) {
      setFileComponent(true);
    } else {
      setSecondPage(!isSecondPage);
    }
  };

  const handleStaticValues = (name, value) => {
    setValues({ ...values, [name]: value });
  };

  const handleSubFolderData = data => {
    setChildFolder(data);
  };

  const convertContentToHTML = name => {
    const currentContentAsHTML = JSON.stringify(
      convertToRaw(editorState[name].getCurrentContent() || ''),
    );
    setConvertedContent({
      ...convertedContent,
      [name]: currentContentAsHTML,
    });
  };

  const handleEditorChange = (state, name) => {
    setEditorState({ ...editorState, [name]: state });
    convertContentToHTML(name);
  };

  const handleDynamicChange = (name, value) => {
    setDynamicValues({ ...dynamicValues, [`DE:${name}`]: value });
  };

  const handleMultipleValues = (name, value) => {
    setMultipleValues({ ...multipleValues, [`DE:${name}`]: value });
  };

  const handleDateChange = (name, value) => {
    const newTime = new Date();
    const defaultTime = newTime.setHours(0, 0, 0, 0);
    setDateValues({
      ...dateValues,
      [name]: { date: value, time: dateValues[name]?.time || defaultTime },
    });
  };
  const handleTimeChange = (name, value) => {
    setDateValues({
      ...dateValues,
      [name]: { time: value, date: dateValues[name]?.date },
    });
  };

  const handleChangeSelect = (selectedOption, name) => {
    const selectedArray = [];
    selectedOption.map(optionObject => selectedArray.push(optionObject.value));
    handleMultipleValues(name, selectedArray);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const dateAndTime = dateValues;
    Object.keys(dateValues).forEach(element => {
      dateAndTime[element] = `${format(
        dateValues[element].date,
        'yyyy-MM-dd',
      )}T${format(dateValues[element].time, 'HH:mm:ss')}`;
    });
    const queryParams = {
      ownerID: user,
      templateID: templates.selected,
      method: 'post',
      ...dateAndTime,
      ...values,
      ...dynamicValues,
    };

    const wfProjectCreationResponse = await actionWebInvoke(
      actions['workfront-action'],
      {},
      { objCode: OBJCODE.PROJECT, queryParams },
    );
    if (wfProjectCreationResponse?.ID !== null) {
      // const projFolderName = values.name.replace(/\s+/g, '-').toLowerCase();
      await createFolderInAEM(
        getHeaders(ims),
        values.name,
        `cats-projects/${wfProjectCreationResponse.ID}`,
      );
      await createFolderInAEM(
        getHeaders(ims),
        'Final Assets',
        `cats-projects/${wfProjectCreationResponse.ID}/final-assets`,
      );
      const workfrontFilesAndFolders = await getWfFoldersAndFiles(
        wfProjectCreationResponse.objCode,
        wfProjectCreationResponse.ID,
      );

      const aemFilesAndFolders = await getAEMFoldersAndFiles(
        getHeaders(ims),
        `cats-projects/${wfProjectCreationResponse.ID}`,
      );
      const filterFilesAndFolders = workfrontFilesAndFolders?.filter(
        element =>
          element.parentID === null &&
          element.enteredByID === element.sysUserID,
      );
      setWorkfrontUrl(
        `${wfProjectCreationResponse.workfrontEndpoint}/project/${wfProjectCreationResponse.ID}/overview`,
      );
      handleRootFolderData(
        filterFilesAndFolders,
        aemFilesAndFolders,
        wfProjectCreationResponse.objCode,
        wfProjectCreationResponse?.ID,
        setRootFolder,
      );
      setWfObjectCodeAndID({
        ...wfObjectCodeAndID,
        objID: wfProjectCreationResponse?.ID,
      });

      setFileComponent(true);
      setIsLoading(false);
    }

    if (multipleValues) {
      const queryParams2 = {
        method: 'put',
        updates: JSON.stringify(multipleValues),
      };

      await actionWebInvoke(
        actions['workfront-action'],
        {},
        {
          objCode: OBJCODE.PROJECT,
          objID: wfProjectCreationResponse.ID,
          queryParams: queryParams2,
        },
      );
    }

    if (convertedContent) {
      const richTextPromises = Object.keys(convertedContent).map(element =>
        actionWebInvoke(
          actions['workfront-action'],
          {},
          {
            objCode: OBJCODE.PROJECT,
            objID: wfProjectCreationResponse.ID,
            bodyData: `{"DE:${element}":${JSON.stringify(
              convertedContent[element],
            )}}`,
          },
        ),
      );
      await Promise.all(richTextPromises);
    }
  };

  const selectTemplateAndFetchCategories = selectedId => {
    setTemplates({ ...templates, selected: selectedId });
  };

  async function fetchTemplates() {
    setTemplates({ ...templates, isLoading: true });

    const fields = ['ID', 'name'];
    const queryParams = { name_Sort: 'asc' };
    const list = await actionWebInvoke(
      actions['workfront-action'],
      {},
      { objCode: OBJCODE.TEMPLATE, queryParams, fields },
    );

    setTemplates({
      ...templates,
      elements: list,
      isLoading: false,
    });
  }

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(async () => {
    if (templates.selected.length > 0) {
      const { selected } = templates;
      const tempQueryParams = {
        ID: selected,
      };
      const fields = ['objectCategories:category'];
      const categories = await actionWebInvoke(
        actions['workfront-action'],
        {},
        { objCode: OBJCODE.TEMPLATE, queryParams: tempQueryParams, fields },
      );
      const data = await fetchCustomFormsAndFields(categories, false);
      setProjectCustomFields(data);
    }
  }, [templates.selected]);

  return isLoading ? (
    <Flex justifyContent="center" marginY="size-1000">
      <ProgressCircle aria-label="Loading…" size="L" isIndeterminate />
    </Flex>
  ) : (
    <Wrap>
      {isFileComponent ? (
        <ContainerBlock>
          <FileComponent
            ims={ims}
            wfObjectCodeAndID={wfObjectCodeAndID}
            assetsFolder={childFolder?.length ? childFolder : rootFolder}
            handleSubFolderData={handleSubFolderData}
            workfrontUrl={workfrontUrl}
            rootFolder={rootFolder}
          />
        </ContainerBlock>
      ) : (
        <Container>
          <Title>
            <h3>Start a Project</h3>
            <Button
              top="15px"
              right="10px"
              variant="cta"
              isDisabled={!isFileComponent}
              onPress={handleSubmit}
            >
              Submit
            </Button>
          </Title>
          {isSecondPage ? (
            <>
              {projectCustomFields &&
                projectCustomFields?.map(({ parameter }, index) => {
                  return (
                    <CustomField
                      key={parameter + index}
                      isProjectForm
                      parameter={parameter}
                      setDateObject={setDateObject}
                      handleDateChange={handleDateChange}
                      dateObject={dateObject}
                      handleTimeChange={handleTimeChange}
                      dynamicValues={dynamicValues}
                      handleDynamicChange={handleDynamicChange}
                      editorState={editorState}
                      handleMultipleValues={handleMultipleValues}
                      handleChangeSelect={handleChangeSelect}
                      handleEditorChange={handleEditorChange}
                    />
                  );
                })}
              <Section>
                <Actions>
                  <Button variant="cta" onPress={handlePageChange}>
                    Previous
                  </Button>
                  <Button variant="cta" onPress={handleSubmit}>
                    Next
                  </Button>
                  <Page>Page 2 of 3</Page>
                </Actions>
              </Section>
            </>
          ) : (
            <StaticProjectForm
              templates={templates}
              selectTemplateAndFetchCategories={
                selectTemplateAndFetchCategories
              }
              handlePageChange={handlePageChange}
              values={values}
              selectedValue={templates.selected}
              handleStaticValues={handleStaticValues}
              handleDateChange={handleDateChange}
              handleTimeChange={handleTimeChange}
              projectCustomFields={projectCustomFields}
            />
          )}
        </Container>
      )}
    </Wrap>
  );
};

DynamicProjectForm.propTypes = {
  user: PropTypes.string,
  ims: PropTypes.shape({
    token: PropTypes.string,
    org: PropTypes.string,
  }),
};

DynamicProjectForm.defaultProps = {
  user: '',
  ims: {
    token: '',
    org: '',
  },
};

export default DynamicProjectForm;
